import type { StringToken, Token } from '@parser';
import type { CadetParser } from './CadetParser';
import * as TT from './CadetTokens';
import { Item } from './Item';
import type { GroupStatement } from './Statements';
import type { Unparser } from './Unparser';
import { ObjectValue } from './Values';

export abstract class Directive extends Item {
  name: string;

  constructor(name: string, opts?: { start?: number; parent?: GroupStatement }) {
    super(opts);
    this.name = name;
  }

  static parseDirective(p: CadetParser): Directive | undefined {
    if (p.current.type !== TT.DIRECTIVE) {
      return;
    }
    let directive: Directive | undefined;

    // --- Try parsing Element Directives ---
    directive = NoteDirective.parse(p);
    if (directive) {
      return directive;
    }
    directive = AssistantNoteDirective.parse(p);
    if (directive) {
      return directive;
    }
    directive = BoundsDirective.parse(p);
    if (directive) {
      return directive;
    }
    directive = PathDirective.parse(p);
    if (directive) {
      return directive;
    }
    directive = StateDirective.parse(p);
    if (directive) {
      return directive;
    }

    // --- Try parsing Document/Global Directives ---
    directive = ImageDirective.parse(p);
    if (directive) {
      return directive;
    }
    // Experimental: Include directive parsing (Phase B start)
    directive = IncludeDirective.parse(p);
    if (directive) {
      return directive;
    }
    directive = SettingsDirective.parse(p);
    if (directive) {
      return directive;
    }

    // --- Try parsing Class Directives ---
    directive = UnitClassDirective.parse(p);
    if (directive) {
      return directive;
    }
    directive = EdgeClassDirective.parse(p);
    if (directive) {
      return directive;
    }
    directive = GroupClassDirective.parse(p);
    if (directive) {
      return directive;
    }

    // If none matched
    throw p.error(`Unknown directive ${p.current.text}`, p.current.start);
  }

  toJSON(opts?: { compact?: boolean }): object {
    return {
      ...super.toJSON(opts),
      name: this.name
    };
  }
}

export abstract class ElementDirective extends Directive {
  id: StringToken;

  constructor(
    name: string,
    id: StringToken,
    opts?: { start?: number; parent?: GroupStatement }
  ) {
    super(name, opts);
    this.id = id;
  }

  toJSON(opts?: { compact?: boolean }): object {
    return {
      ...super.toJSON(opts),
      id: this.id
    };
  }
}

export class BoundsDirective extends ElementDirective {
  bounds: Token<string>;

  constructor(
    id: StringToken,
    bounds: Token<string>,
    opts?: { start?: number; parent?: GroupStatement }
  ) {
    super(':BOUNDS', id, opts);
    this.bounds = bounds;
  }

  // :BOUNDS fuid string BREAK
  static parse(p: CadetParser): BoundsDirective | undefined {
    return p.withSavedPosition(commit => {
      const token = consumeDirectiveToken(p, 'BOUNDS');
      if (token === undefined) {
        return;
      }
      const id = p.identifier();
      if (id === undefined) {
        throw p.error('Expected element identifier', p.current.start);
      }
      const bounds = p.string();
      if (bounds === undefined) {
        throw p.error('Expected string', p.current.start);
      }
      commit();
      return new BoundsDirective(id, bounds, {
        start: token.start,
        parent: p.currentParent
      });
    });
  }

  toJSON(opts?: { compact?: boolean }): object {
    return {
      ...super.toJSON(opts),
      bounds: this.bounds.toJSON(opts)
    };
  }

  unparse(u: Unparser): void {
    u.add(this.name);
    u.add(this.id.text);
    u.add(this.bounds.text);
  }
}

export class PathDirective extends ElementDirective {
  path: Token<string>;

  constructor(
    id: StringToken,
    path: Token<string>,
    opts?: { start?: number; parent?: GroupStatement }
  ) {
    super(':PATH', id, opts);
    this.path = path;
  }

  // :PATH fuid string BREAK
  static parse(p: CadetParser): PathDirective | undefined {
    return p.withSavedPosition(commit => {
      const token = consumeDirectiveToken(p, 'PATH');
      if (token === undefined) {
        return;
      }
      const id = p.identifier();
      if (id === undefined) {
        throw p.error('Expected element identifier', p.current.start);
      }
      const path = p.string();
      if (path === undefined) {
        throw p.error('Expected string', p.current.start);
      }
      commit();
      return new PathDirective(id, path, {
        start: token.start,
        parent: p.currentParent
      });
    });
  }

  toJSON(opts?: { compact?: boolean }): object {
    return {
      ...super.toJSON(opts),
      path: this.path.toJSON(opts)
    };
  }

  unparse(u: Unparser): void {
    u.add(this.name);
    u.add(this.id.text);
    u.add(this.path.text);
  }
}

export class NoteDirective extends ElementDirective {
  noteText: Token<string>;

  constructor(
    id: StringToken,
    noteText: Token<string>,
    opts?: { start?: number; parent?: GroupStatement }
  ) {
    super(':NOTE', id, opts);
    this.noteText = noteText;
  }

  // :NOTE fuid string BREAK
  static parse(p: CadetParser): NoteDirective | undefined {
    return p.withSavedPosition(commit => {
      const token = consumeDirectiveToken(p, 'NOTE');
      if (token === undefined) {
        return;
      }
      const id = p.identifier();
      if (id === undefined) {
        throw p.error('Expected element identifier', p.current.start);
      }
      const text = p.iString();
      if (text === undefined) {
        throw p.error('Expected string', p.current.start);
      }
      commit();
      return new NoteDirective(id, text, {
        start: token.start,
        parent: p.currentParent
      });
    });
  }

  toJSON(opts?: { compact?: boolean }): object {
    return {
      ...super.toJSON(opts),
      noteText: this.noteText.toJSON(opts)
    };
  }

  unparse(u: Unparser): void {
    u.add(this.name);
    u.add(this.id.text);
    u.add(this.noteText.text);
  }
}

export class AssistantNoteDirective extends ElementDirective {
  noteText: Token<string>;

  constructor(
    id: StringToken,
    noteText: Token<string>,
    opts?: { start?: number; parent?: GroupStatement }
  ) {
    super(':ASSISTANT', id, opts);
    this.noteText = noteText;
  }

  // :ASSISTANT fuid string BREAK
  static parse(p: CadetParser): AssistantNoteDirective | undefined {
    return p.withSavedPosition(commit => {
      const token = consumeDirectiveToken(p, 'ASSISTANT');
      if (token === undefined) {
        return;
      }
      const id = p.identifier();
      if (id === undefined) {
        throw p.error('Expected element identifier', p.current.start);
      }
      const text = p.iString();
      if (text === undefined) {
        throw p.error('Expected string', p.current.start);
      }
      commit();
      return new AssistantNoteDirective(id, text, {
        start: token.start,
        parent: p.currentParent
      });
    });
  }

  toJSON(opts?: { compact?: boolean }): object {
    return {
      ...super.toJSON(opts),
      noteText: this.noteText.toJSON(opts)
    };
  }

  unparse(u: Unparser): void {
    u.add(this.name);
    u.add(this.id.text);
    u.add(this.noteText.text);
  }
}

export class ClassDirective extends Directive {
  cls: StringToken;
  superCls?: StringToken;
  value: ObjectValue;

  constructor(
    name: string,
    cls: StringToken,
    value: ObjectValue,
    opts?: { start?: number; parent?: GroupStatement; superCls?: StringToken }
  ) {
    super(name, opts);
    this.cls = cls;
    this.superCls = opts?.superCls;
    this.value = value;
  }

  toJSON(opts?: { compact?: boolean }): object {
    return {
      ...super.toJSON(opts),
      superCls: this.superCls?.toJSON(opts),
      value: this.value.toJSON(opts)
    };
  }

  unparse(u: Unparser): void {
    u.add(this.name);
    u.add(this.cls.text);
    if (this.superCls) {
      u.add(':');
      u.add(this.superCls.text);
    }
    this.value.unparse(u);
  }
}

export class UnitClassDirective extends ClassDirective {
  constructor(
    cls: StringToken,
    value: ObjectValue,
    opts?: { start?: number; parent?: GroupStatement; superCls?: StringToken }
  ) {
    super(':UNIT', cls, value, opts);
  }

  // :UNIT classIdentifier [colon classIdentifier] ObjectValue
  static parse(p: CadetParser): UnitClassDirective | undefined {
    return p.withSavedPosition(commit => {
      const token = consumeDirectiveToken(p, 'UNIT');
      if (token === undefined) {
        return;
      }
      const cls = p.classIdentifier();
      if (cls === undefined) {
        return;
      }
      let superCls: StringToken | undefined;
      if (p.consumeToken(TT.COLON)) {
        superCls = p.classIdentifier();
        if (superCls === undefined) {
          return;
        }
      }
      const objectValue = ObjectValue.parse(p, p.currentParent);
      if (objectValue === undefined) {
        return;
      }
      commit();
      return new UnitClassDirective(cls, objectValue, {
        start: token.start,
        parent: p.currentParent,
        superCls
      });
    });
  }
}

export class EdgeClassDirective extends ClassDirective {
  constructor(
    cls: StringToken,
    value: ObjectValue,
    opts?: { start?: number; parent?: GroupStatement; superCls?: StringToken }
  ) {
    super(':EDGE', cls, value, opts);
  }

  // :EDGE arrow [colon arrow] ObjectValue
  static parse(p: CadetParser): EdgeClassDirective | undefined {
    return p.withSavedPosition(commit => {
      const token = consumeDirectiveToken(p, 'EDGE');
      if (token === undefined) {
        return;
      }
      const cls = p.arrow() ?? p.classIdentifier();
      if (cls === undefined) {
        return;
      }
      let superCls: StringToken | undefined;
      if (p.consumeToken(TT.COLON)) {
        superCls = p.arrow() ?? p.classIdentifier();
        if (superCls === undefined) {
          return;
        }
      }
      const objectValue = ObjectValue.parse(p, p.currentParent);
      if (objectValue === undefined) {
        return;
      }
      commit();
      return new EdgeClassDirective(cls, objectValue, {
        start: token.start,
        parent: p.currentParent,
        superCls
      });
    });
  }
}

export class GroupClassDirective extends ClassDirective {
  constructor(
    cls: StringToken,
    value: ObjectValue,
    opts?: { start?: number; parent?: GroupStatement; superCls?: StringToken }
  ) {
    super(':GROUP', cls, value, opts);
  }

  // :GROUP classIdentifier [colon classIdentifier] ObjectValue
  static parse(p: CadetParser): UnitClassDirective | undefined {
    return p.withSavedPosition(commit => {
      const token = consumeDirectiveToken(p, 'GROUP');
      if (token === undefined) {
        return;
      }
      const cls = p.classIdentifier();
      if (cls === undefined) {
        return;
      }
      let superCls: StringToken | undefined;
      if (p.consumeToken(TT.COLON)) {
        superCls = p.classIdentifier();
        if (superCls === undefined) {
          return;
        }
      }
      const objectValue = ObjectValue.parse(p, p.currentParent);
      if (objectValue === undefined) {
        return;
      }
      commit();
      return new GroupClassDirective(cls, objectValue, {
        start: token.start,
        parent: p.currentParent,
        superCls
      });
    });
  }
}

export class SettingsDirective extends Directive {
  value: ObjectValue; // Holds the parsed { key: value, ... } content

  constructor(value: ObjectValue, opts?: { start?: number; parent?: GroupStatement }) {
    super(':SETTINGS', opts); // Directive name is hardcoded
    this.value = value;
  }

  // :SETTINGS ObjectValue
  static parse(p: CadetParser): SettingsDirective | undefined {
    return p.withSavedPosition(commit => {
      // Consume the :SETTINGS token
      const token = consumeDirectiveToken(p, 'SETTINGS');
      if (token === undefined) {
        return undefined; // Not a :SETTINGS directive
      }

      // Parse the mandatory ObjectValue that follows
      const objectValue = ObjectValue.parse(p, p.currentParent);
      if (objectValue === undefined) {
        // If parsing the object fails, throw an error
        throw p.error("Expected object literal '{...}' after :SETTINGS", p.current.start);
      }

      // Successfully parsed :SETTINGS { ... }
      commit();
      return new SettingsDirective(objectValue, {
        start: token.start,
        parent: p.currentParent
      });
    });
  }

  toJSON(opts?: { compact?: boolean }): object {
    return {
      ...super.toJSON(opts),
      value: this.value.toJSON(opts)
    };
  }

  unparse(u: Unparser): void {
    u.add(this.name); // Adds ":SETTINGS"
    this.value.unparse(u); // Let ObjectValue handle its formatting
  }
}

export class ImageDirective extends Directive {
  imageId: StringToken;
  value: Token<string>;

  constructor(
    imageId: StringToken,
    value: Token<string>,
    opts?: { start?: number; parent?: GroupStatement }
  ) {
    super(':IMAGE', opts);
    this.imageId = imageId;
    this.value = value;
  }

  // :IMAGE imageId string
  static parse(p: CadetParser): ImageDirective | undefined {
    return p.withSavedPosition(commit => {
      const token = consumeDirectiveToken(p, 'IMAGE');
      if (token === undefined) {
        return;
      }
      const id = p.identifier();
      if (id === undefined) {
        throw p.error('Expected image identifier after :IMAGE', p.current.start);
      }
      const value = p.string();
      if (value === undefined) {
        throw p.error('Expected string after :IMAGE imageId', p.current.start);
      }
      commit();
      return new ImageDirective(id, value, {
        start: token.start,
        parent: p.currentParent
      });
    });
  }

  toJSON(opts?: { compact?: boolean }): object {
    return {
      ...super.toJSON(opts),
      imageId: this.imageId.toJSON(opts),
      value: this.value.toJSON(opts)
    };
  }

  unparse(u: Unparser): void {
    u.add(this.name);
    u.add(this.imageId.text);
    u.add(this.value.text);
  }
}

export class StateDirective extends ElementDirective {
  value: ObjectValue; // Holds the parsed { key: value, ... } content

  constructor(
    id: StringToken,
    value: ObjectValue,
    opts?: { start?: number; parent?: GroupStatement }
  ) {
    super(':STATE', id, opts); // Directive name is hardcoded
    this.value = value;
  }

  // :STATE elementId ObjectValue
  static parse(p: CadetParser): StateDirective | undefined {
    return p.withSavedPosition(commit => {
      // Consume the :STATE token
      const token = consumeDirectiveToken(p, 'STATE');
      if (token === undefined) {
        return undefined; // Not a :STATE directive
      }

      // Consume the target element ID
      const id = p.identifier();
      if (id === undefined) {
        throw p.error('Expected element identifier after :STATE', p.current.start);
      }

      // Parse the mandatory ObjectValue that follows
      const objectValue = ObjectValue.parse(p, p.currentParent);
      if (objectValue === undefined) {
        throw p.error(
          "Expected object literal '{...}' after :STATE elementID",
          p.current.start
        );
      }

      // Successfully parsed :STATE elementId { ... }
      commit();
      return new StateDirective(id, objectValue, {
        start: token.start,
        parent: p.currentParent
      });
    });
  }

  toJSON(opts?: { compact?: boolean }): object {
    return {
      ...super.toJSON(opts),
      value: this.value.toJSON(opts)
    };
  }

  unparse(u: Unparser): void {
    u.add(this.name); // Adds ":STATE"
    u.add(this.id.text); // Add elementId
    this.value.unparse(u); // Let ObjectValue handle formatting
  }
}

export class IncludeDirective extends Directive {
  symbol: StringToken;

  constructor(symbol: StringToken, opts?: { start?: number; parent?: GroupStatement }) {
    super(':INCLUDE', opts);
    this.symbol = symbol;
  }

  // :INCLUDE identifier
  static parse(p: CadetParser): IncludeDirective | undefined {
    return p.withSavedPosition(commit => {
      const token = consumeDirectiveToken(p, 'INCLUDE');
      if (token === undefined) {
        return;
      }
      const sym = p.identifier();
      if (sym === undefined) {
        throw p.error('Expected include symbol after :INCLUDE', p.current.start);
      }
      commit();
      return new IncludeDirective(sym, {
        start: token.start,
        parent: p.currentParent
      });
    });
  }

  toJSON(opts?: { compact?: boolean }): object {
    return {
      ...super.toJSON(opts),
      symbol: this.symbol.toJSON(opts)
    };
  }

  unparse(u: Unparser): void {
    u.add(this.name);
    u.add(this.symbol.text);
  }
}

function consumeDirectiveToken(p: CadetParser, name: string): Token | undefined {
  return p.withSavedPosition(commit => {
    const token = p.consumeToken(TT.DIRECTIVE);
    if (token === undefined) {
      return;
    }
    if (typeof token.value !== 'string') {
      return;
    }
    if (token.value !== name) {
      return;
    }
    commit();
    return token;
  });
}
